import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { reportLovableError } from "../lib/lovable-error-reporting";
import { AppStoreProvider } from "../lib/app-store";
import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="app-viewport flex items-center justify-center bg-bg px-4">
      <div className="max-w-md text-center">
        <h1 className="text-6xl font-semibold text-fg">404</h1>
        <p className="mt-3 text-sm text-muted">这个页面不存在</p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-[var(--r-md)] bg-accent px-4 py-2 text-sm font-medium text-accent-foreground"
          >
            返回登录
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="app-viewport flex items-center justify-center bg-bg px-4">
      <div className="max-w-sm text-center">
        <h1 className="text-lg font-semibold">页面加载失败</h1>
        <p className="mt-2 text-sm text-muted">请稍后重试。</p>
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="rounded-[var(--r-md)] bg-accent px-4 py-2 text-sm font-medium text-accent-foreground"
          >
            重试
          </button>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  // iOS/WeChat: restore scroll after input blur
  useEffect(() => {
    const handler = (e: FocusEvent) => {
      const t = e.target as HTMLElement;
      if (
        t &&
        (t.tagName === "INPUT" || t.tagName === "TEXTAREA")
      ) {
        setTimeout(() => window.scrollTo(0, 0), 0);
      }
    };
    document.addEventListener("focusout", handler);
    return () => document.removeEventListener("focusout", handler);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppStoreProvider>
        <Outlet />
        <Toaster position="top-center" />
      </AppStoreProvider>
    </QueryClientProvider>
  );
}
